import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import IconButton from 'components/stateless/Button/IconButton';
import {
  FormBorder,
  FormContents,
  FormInput,
  FormRadio,
  FormRadioWrap,
  FormSelect,
  FormSubtitle,
  NumericFormatInput,
} from 'components/stateless/CommonForm';
import { FormErrorMessage } from 'components/stateless/CommonForm/FormErrorMessage';
import { BackGroundType } from 'components/stateless/CommonForm/FormSubtitle';
import Value from 'components/stateless/Form/Value/Value';
import { BackHeaderTitle } from 'components/stateless/Title/BackHeaderTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import getSelectOptions from 'constants/selectOptions';
import type { CURRENCY_TYPE } from 'enums';
import { DOCUMENT_CONDITION_AMOUNT_RANGE, DOCUMENT_CONDITION_TYPE } from 'enums';
import useMounted from 'hooks/useMounted';
import type { LoanAdditionalDocumentVOModel } from 'models/vo/LoanAdditionalDocumentVO';
import {
  requestFinancierDeleteLoanAdditionalDocument,
  requestFinancierLoanAdditionalDocumentDetail,
  requestFinancierModifyLoanAdditionalDocument,
} from 'utils/http/api/financier/loan-additional-document-attachments';
import type { UpdateFiLoanAdditionalDocumentDTO } from 'utils/http/api/financier/loan-additional-document-attachments/request';
import { ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { requestDTOParser } from 'utils/valueManager/ValueManager';

const FinancierAdditionalDocumentDetail = () => {
  const { t } = useTranslation();
  const modal = useModal();
  const mounted = useMounted();
  const history = useHistory();
  const { financierDocumentFormId } = useParams<any>();

  const { register, errors, setValue, handleSubmit, watch, control, reset, clearErrors, getValues } =
    useForm<UpdateFiLoanAdditionalDocumentDTO>();

  const { mandatoryCondition, hardCopyCondition } = watch();

  const [additionalDocumentInfo, setAdditionalDocumentInfo] = useState<LoanAdditionalDocumentVOModel>();
  const [isEditable, setEditable] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');

  const isFieldRequired = (filedValue: string) =>
    String(filedValue) === 'true' ? t('text:This_information_is_required') : false;

  useEffect(() => {
    fetchAll();
  }, [mounted]);

  const fetchAll = async () => {
    const fetchedAdditionalDocumentInfo = await requestFinancierLoanAdditionalDocumentDetail(financierDocumentFormId);

    const {
      documentFormName,
      activated,
      description,
      noticeForPartner,
      mandatoryCondition,
      hardCopyCondition,
      fileName,
    } = fetchedAdditionalDocumentInfo;

    reset({
      documentFormName,
      activated: String(activated),
      description,
      noticeForPartner,
      mandatoryCondition: {
        ...mandatoryCondition,
        required: String(mandatoryCondition.required),
        useSpecificCondition: String(mandatoryCondition.useSpecificCondition),
        amount: mandatoryCondition.amount ?? '',
      },
      hardCopyCondition: {
        ...hardCopyCondition,
        required: String(hardCopyCondition.required),
        useSpecificCondition: String(hardCopyCondition.useSpecificCondition),
        amount: hardCopyCondition.amount ?? '',
      },
    });

    ReactDOM.unstable_batchedUpdates(() => {
      setAdditionalDocumentInfo(fetchedAdditionalDocumentInfo);
      setFileName(fileName);
    });
  };

  const handleClickEdit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    setEditable(true);
  };

  const handleClickDelete = () => {
    const showDeleteModal = (): void => {
      modal.show(<h6>{t('text:The_template_setting_has_been_deleted')}</h6>, {
        closeBtnText: t('text:OK'),
      });
    };

    const handleConfirmBtn = async () => {
      try {
        await requestFinancierDeleteLoanAdditionalDocument(financierDocumentFormId);
        showDeleteModal();
        history.push(ROUTES_FI.PROGRAM_SETTINGS.DOCUMENT_LIST);
      } catch (e) {
        modal.show(e);
      }
    };

    modal.show(
      <h6>
        {t('text:Would_you_like_to_delete_the_template?')}
        <br />
        {t('text:Deleted_information_cannot_be_reversed')}
      </h6>,
      {
        modalType: ModalType.CONFIRM,
        title: t('text:Delete_Document_Type'),
        confirmBtnCb: handleConfirmBtn,
      },
    );
  };

  const handleClickCancel = () => {
    const handleConfirm = () => {
      if (additionalDocumentInfo) {
        const {
          documentFormName,
          activated,
          description,
          noticeForPartner,
          mandatoryCondition,
          hardCopyCondition,
          fileName,
        } = additionalDocumentInfo;

        reset({
          documentFormName,
          activated: String(activated),
          description,
          noticeForPartner,
          mandatoryCondition: {
            ...mandatoryCondition,
            required: String(mandatoryCondition.required),
            useSpecificCondition: String(mandatoryCondition.useSpecificCondition),
          },
          hardCopyCondition: {
            ...hardCopyCondition,
            required: String(hardCopyCondition.required),
            useSpecificCondition: String(hardCopyCondition.useSpecificCondition),
          },
        });
        setFileName(fileName);
      }
      clearErrors();
      setEditable(false);
    };

    modal.show(
      <h6>
        {t('text:Would_you_like_to_cancel_the_registration?')}
        <br />
        {t('text:The_information_will_not_be_saved_if_registration_is_cancelled')}
      </h6>,
      {
        modalType: ModalType.CONFIRM,
        closeBtnText: t('text:Cancel'),
        confirmBtnCb: handleConfirm,
      },
    );
  };

  const setFileNameToUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const [file] = e.target.files;
      if (file?.name) setFileName(file.name);
    }
  };

  const handleClickRemoveFile = (e: any) => {
    e.preventDefault();

    setFileName('');
    setValue('file', null);
  };

  const handleClickSaveBtn = async (data: UpdateFiLoanAdditionalDocumentDTO, e: any) => {
    e.preventDefault();

    const showModifyModal = (): void => {
      modal.show(<h6>{t('text:Document_has_been_modified_successfully')}</h6>, {
        closeBtnText: t('text:OK'),
        closeBtnCb: async () => {
          setEditable(false);
          await fetchAll();
        },
      });
    };

    try {
      requestDTOParser(data);
      if (additionalDocumentInfo) {
        await requestFinancierModifyLoanAdditionalDocument(additionalDocumentInfo?.loanAdditionalDocumentId, data);
      }
      showModifyModal();
    } catch (error) {
      modal.show(error);
    }
  };

  const handleChangeMandatoryUseSpecificCondition = () => {
    setValue('mandatoryCondition.documentConditionType', null);
    setValue('mandatoryCondition.documentConditionAmountRange', null);
    setValue('mandatoryCondition.amount', '');
    setValue('mandatoryCondition.currencyType', null);
    clearErrors([
      'mandatoryCondition.documentConditionType',
      'mandatoryCondition.documentConditionAmountRange',
      'mandatoryCondition.amount',
      'mandatoryCondition.currencyType',
    ]);
  };

  const handleChangeHardCopyUseSpecificCondition = () => {
    setValue('hardCopyCondition.documentConditionType', null);
    setValue('hardCopyCondition.documentConditionAmountRange', null);
    setValue('hardCopyCondition.amount', '');
    setValue('hardCopyCondition.currencyType', null);
    clearErrors([
      'hardCopyCondition.documentConditionType',
      'hardCopyCondition.documentConditionAmountRange',
      'hardCopyCondition.amount',
      'hardCopyCondition.currencyType',
    ]);
  };

  const currencyTypeValidate = () => {
    if (
      String(mandatoryCondition.useSpecificCondition) === 'true' &&
      String(hardCopyCondition.useSpecificCondition) === 'true'
    ) {
      if (getValues('mandatoryCondition.currencyType') !== getValues('hardCopyCondition.currencyType')) {
        return t('text:Each_condition_has_a_different_currency');
      }
    }
    clearErrors(['mandatoryCondition.currencyType', 'hardCopyCondition.currencyType']);

    return true;
  };

  const renderEditSaveBtn = () => {
    return !isEditable ? (
      <div className="flex-end mt-4">
        <Button
          size={ButtonSizeEnum.LG}
          variant={ButtonVariantEnum.OUTLINED}
          color={ButtonColorEnum.SECONDARY}
          onClick={handleClickDelete}
        >
          {t('text:Delete')}
        </Button>
        <Button size={ButtonSizeEnum.LG} className="ms-2" onClick={handleClickEdit}>
          {t('text:Edit')}
        </Button>
      </div>
    ) : (
      <div className="flex-end mt-4">
        <Button
          size={ButtonSizeEnum.LG}
          variant={ButtonVariantEnum.OUTLINED}
          color={ButtonColorEnum.SECONDARY}
          onClick={handleClickCancel}
        >
          {t('text:Cancel')}
        </Button>
        <Button
          size={ButtonSizeEnum.LG}
          type="submit"
          className="ms-2"
          onClick={handleSubmit((data, e) => handleClickSaveBtn(data, e))}
        >
          {t('text:Save')}
        </Button>
      </div>
    );
  };

  return (
    <form>
      <BackHeaderTitle title={t('text:Additional_Document_Details')} />
      <div className="content-area">
        <FormBorder>
          <FormSubtitle backGroundType={BackGroundType.DarkGray} title={t('text:Template_Information')} />
          <FormContents>
            <div className="row d-flex justify-content-between">
              <FormInput
                label={t('text:Template_Name')}
                name="documentFormName"
                defaultValue={additionalDocumentInfo?.documentFormName}
                ref={register({ required: t('text:This_information_is_required') })}
                error={errors['documentFormName']}
                disabled={!isEditable}
                requiredOptions={{ required: true }}
                isShouldShowDash
              />
              <FormRadioWrap
                col={5}
                label={t('text:Template_Status')}
                editable={isEditable}
                required
                error={errors['activated']}
              >
                <FormRadio
                  disabled={!isEditable}
                  name="activated"
                  value="true"
                  label={t('text:Active')}
                  ref={register({ required: true })}
                />
                <FormRadio
                  disabled={!isEditable}
                  name="activated"
                  value="false"
                  label={t('text:Suspended')}
                  ref={register({ required: true })}
                />
              </FormRadioWrap>
            </div>
            <div className="row">
              <FormInput
                col={12}
                name="description"
                defaultValue={additionalDocumentInfo?.description}
                ref={register()}
                label={t('text:Template_Description')}
                error={errors['description']}
                disabled={!isEditable}
                isShouldShowDash
              />
            </div>
            <div className="row">
              <label className="information-form__label">{t('text:Notice_for_Partner_User')}</label>
              <div>
                {isEditable || additionalDocumentInfo?.noticeForPartner ? (
                  <textarea
                    name="noticeForPartner"
                    ref={register()}
                    disabled={!isEditable}
                    className={clsx('information-form__input overflow-scroll', {
                      'error-input-border': errors['noticeForPartner'],
                    })}
                    placeholder={t('text:Please_type_here')}
                  />
                ) : (
                  <Value value="-" />
                )}
                <div className="p-1">
                  <FormErrorMessage error={errors['noticeForPartner']} />
                </div>
              </div>
            </div>
          </FormContents>
        </FormBorder>
      </div>
      <div className="content-area">
        <FormBorder>
          <FormSubtitle backGroundType={BackGroundType.DarkGray} title={t('text:Template_Condition')} />
          <FormContents>
            <div className="row">
              <FormRadioWrap
                col={6}
                label={t('text:Is_a_Mandatory_Documents?')}
                editable={isEditable}
                required
                error={errors.mandatoryCondition?.required}
              >
                <FormRadio
                  disabled={!isEditable}
                  name="mandatoryCondition.required"
                  value="true"
                  ref={register({ required: true })}
                  label={t('text:Yes')}
                />
                <FormRadio
                  disabled={!isEditable}
                  name="mandatoryCondition.required"
                  value="false"
                  ref={register({ required: true })}
                  label={t('text:No')}
                />
              </FormRadioWrap>
            </div>
            <div className="row">
              <FormRadioWrap
                col={6}
                editable={isEditable}
                label={t('text:Set_the_Specific_Required_Condition_for_Mandatory_Document?')}
                required
                error={errors.mandatoryCondition?.useSpecificCondition}
              >
                <FormRadio
                  disabled={!isEditable}
                  name="mandatoryCondition.useSpecificCondition"
                  onChange={handleChangeMandatoryUseSpecificCondition}
                  value="true"
                  ref={register({ required: true })}
                  label={t('text:Yes')}
                />
                <FormRadio
                  disabled={!isEditable}
                  name="mandatoryCondition.useSpecificCondition"
                  onChange={handleChangeMandatoryUseSpecificCondition}
                  value="false"
                  ref={register({ required: true })}
                  label={t('text:No_Applied_all_loan_application')}
                />
              </FormRadioWrap>
            </div>
            <div
              className={clsx('row d-flex justify-content-around', {
                'd-none': String(mandatoryCondition?.useSpecificCondition) !== 'true',
              })}
            >
              <FormSelect
                col={3}
                name="mandatoryCondition.documentConditionType"
                required
                className={clsx('information-form__input', {
                  placeholder: !mandatoryCondition?.documentConditionType,
                })}
                disabled={!isEditable}
                isShouldShowDash
                defaultValue={additionalDocumentInfo?.mandatoryCondition?.documentConditionType}
                label={t('text:Application_Type')}
                placeholderOptions={{ show: true }}
                selectOptions={[
                  {
                    label: t(`code:document-condition-type.${DOCUMENT_CONDITION_TYPE.SINGLE_AMOUNT}`),
                    value: DOCUMENT_CONDITION_TYPE.SINGLE_AMOUNT,
                  },
                  {
                    label: t(`code:document-condition-type.${DOCUMENT_CONDITION_TYPE.ACCUMULATE_AMOUNT}`),
                    value: DOCUMENT_CONDITION_TYPE.ACCUMULATE_AMOUNT,
                  },
                ]}
                ref={register({ required: isFieldRequired(mandatoryCondition?.useSpecificCondition) })}
                error={errors.mandatoryCondition?.documentConditionType}
              />
              <FormSelect
                col={3}
                name="mandatoryCondition.documentConditionAmountRange"
                required
                className={clsx('information-form__input', {
                  placeholder: !mandatoryCondition?.documentConditionAmountRange,
                })}
                disabled={!isEditable}
                isShouldShowDash
                defaultValue={additionalDocumentInfo?.mandatoryCondition?.documentConditionAmountRange}
                label={t('text:Range')}
                placeholderOptions={{ show: true }}
                selectOptions={[
                  {
                    label: t(`code:document-condition-amount-range.${DOCUMENT_CONDITION_AMOUNT_RANGE.GT}`),
                    value: DOCUMENT_CONDITION_AMOUNT_RANGE.GT,
                  },
                  {
                    label: t(`code:document-condition-amount-range.${DOCUMENT_CONDITION_AMOUNT_RANGE.GOE}`),
                    value: DOCUMENT_CONDITION_AMOUNT_RANGE.GOE,
                  },
                ]}
                ref={register({ required: isFieldRequired(mandatoryCondition?.useSpecificCondition) })}
                error={errors.mandatoryCondition?.documentConditionAmountRange}
              />
              {isEditable || additionalDocumentInfo?.mandatoryCondition.amount ? (
                <NumericFormatInput
                  col={3}
                  disabled={!isEditable}
                  name="mandatoryCondition.amount"
                  control={control}
                  label={t('text:Application_loan_Amount')}
                  required
                  error={errors.mandatoryCondition?.amount}
                  rules={{ required: isFieldRequired(mandatoryCondition?.useSpecificCondition) }}
                />
              ) : (
                <div className="col-3">
                  <label className="information-form__label">{t('text:Application_loan_Amount')}</label>
                  <Value value="-" />
                </div>
              )}
              <FormSelect
                col={3}
                disabled={!isEditable}
                isShouldShowDash
                required
                defaultValue={additionalDocumentInfo?.mandatoryCondition.currencyType}
                name="mandatoryCondition.currencyType"
                className={clsx('information-form__input', {
                  placeholder: !mandatoryCondition?.currencyType,
                })}
                label={t('text:Currency')}
                placeholderOptions={{ show: true }}
                selectOptions={getSelectOptions<CURRENCY_TYPE>('CURRENCY_TYPE')}
                ref={register({
                  required: isFieldRequired(mandatoryCondition?.useSpecificCondition),
                  validate: currencyTypeValidate,
                })}
                error={errors.mandatoryCondition?.currencyType}
              />
            </div>
          </FormContents>
        </FormBorder>
        <FormBorder>
          <FormSubtitle backGroundType={BackGroundType.DarkGray} title={t('text:Hard_copy_Require_Condition')} />
          <FormContents>
            <div className="row">
              <FormRadioWrap
                col={6}
                label={t('text:Is_It_necessary_to_deliver_the_hard_copies?')}
                editable={isEditable}
                required
                error={errors.hardCopyCondition?.required}
              >
                <FormRadio
                  disabled={!isEditable}
                  name="hardCopyCondition.required"
                  value="true"
                  ref={register({ required: true })}
                  label={t('text:Yes')}
                />
                <FormRadio
                  disabled={!isEditable}
                  name="hardCopyCondition.required"
                  value="false"
                  ref={register({ required: true })}
                  label={t('text:No')}
                />
              </FormRadioWrap>
            </div>
            <div className="row">
              <FormRadioWrap
                col={6}
                label={t('text:Set_the_Specific_Required_Condition_for_Template?')}
                editable={isEditable}
                required
                error={errors.hardCopyCondition?.useSpecificCondition}
              >
                <FormRadio
                  disabled={!isEditable}
                  name="hardCopyCondition.useSpecificCondition"
                  onChange={handleChangeHardCopyUseSpecificCondition}
                  value="true"
                  ref={register({ required: true })}
                  label={t('text:Yes')}
                />
                <FormRadio
                  disabled={!isEditable}
                  name="hardCopyCondition.useSpecificCondition"
                  value="false"
                  onChange={handleChangeHardCopyUseSpecificCondition}
                  ref={register({ required: true })}
                  label={t('text:No_Applied_all_loan_application')}
                />
              </FormRadioWrap>
            </div>
            <div
              className={clsx('row d-flex justify-content-around', {
                'd-none': String(hardCopyCondition?.useSpecificCondition) !== 'true',
              })}
            >
              <FormSelect
                col={3}
                name="hardCopyCondition.documentConditionType"
                className={clsx('information-form__input', {
                  placeholder: !hardCopyCondition?.documentConditionType,
                })}
                disabled={!isEditable}
                isShouldShowDash
                required
                label={t('text:Application_Type')}
                defaultValue={additionalDocumentInfo?.hardCopyCondition.documentConditionType}
                placeholderOptions={{ show: true }}
                selectOptions={[
                  {
                    label: t(`code:document-condition-type.${DOCUMENT_CONDITION_TYPE.SINGLE_AMOUNT}`),
                    value: DOCUMENT_CONDITION_TYPE.SINGLE_AMOUNT,
                  },
                  {
                    label: t(`code:document-condition-type.${DOCUMENT_CONDITION_TYPE.ACCUMULATE_AMOUNT}`),
                    value: DOCUMENT_CONDITION_TYPE.ACCUMULATE_AMOUNT,
                  },
                ]}
                ref={register({ required: isFieldRequired(hardCopyCondition?.useSpecificCondition) })}
                error={errors.hardCopyCondition?.documentConditionType}
              />
              <FormSelect
                col={3}
                name="hardCopyCondition.documentConditionAmountRange"
                className={clsx('information-form__input', {
                  placeholder: !hardCopyCondition?.documentConditionAmountRange,
                })}
                disabled={!isEditable}
                isShouldShowDash
                required
                label={t('text:Range')}
                placeholderOptions={{ show: true }}
                defaultValue={additionalDocumentInfo?.hardCopyCondition.documentConditionAmountRange}
                selectOptions={[
                  {
                    label: t(`code:document-condition-amount-range.${DOCUMENT_CONDITION_AMOUNT_RANGE.GT}`),
                    value: DOCUMENT_CONDITION_AMOUNT_RANGE.GT,
                  },
                  {
                    label: t(`code:document-condition-amount-range.${DOCUMENT_CONDITION_AMOUNT_RANGE.GOE}`),
                    value: DOCUMENT_CONDITION_AMOUNT_RANGE.GOE,
                  },
                ]}
                ref={register({ required: isFieldRequired(hardCopyCondition?.useSpecificCondition) })}
                error={errors.hardCopyCondition?.documentConditionAmountRange}
              />
              {isEditable || additionalDocumentInfo?.hardCopyCondition.amount ? (
                <NumericFormatInput
                  col={3}
                  name="hardCopyCondition.amount"
                  control={control}
                  disabled={!isEditable}
                  label={t('text:Application_loan_Amount')}
                  required
                  rules={{ required: isFieldRequired(hardCopyCondition?.useSpecificCondition) }}
                  error={errors.hardCopyCondition?.amount}
                />
              ) : (
                <div className="col-3">
                  <label className="information-form__label">{t('text:Application_loan_Amount')}</label>
                  <Value value="-" />
                </div>
              )}
              <FormSelect
                col={3}
                name="hardCopyCondition.currencyType"
                className={clsx('information-form__input', {
                  placeholder: !hardCopyCondition?.currencyType,
                })}
                disabled={!isEditable}
                isShouldShowDash
                required
                label={t('text:Currency')}
                placeholderOptions={{ show: true }}
                defaultValue={additionalDocumentInfo?.hardCopyCondition.currencyType}
                selectOptions={getSelectOptions<CURRENCY_TYPE>('CURRENCY_TYPE')}
                ref={register({
                  required: isFieldRequired(hardCopyCondition?.useSpecificCondition),
                  validate: currencyTypeValidate,
                })}
                error={errors.hardCopyCondition?.currencyType}
              />
            </div>
          </FormContents>
        </FormBorder>
      </div>
      <div className="content-area">
        <FormBorder>
          <FormSubtitle backGroundType={BackGroundType.DarkGray} title={t('text:Template_File_Upload')} isRequired />
          <div className="detail-in-file-upload-form bg-sub100">
            <div className="d-flex ps-3">
              <input
                className="d-none"
                onChange={setFileNameToUpload}
                ref={register()}
                type="file"
                name="formAttachment"
                id="FileUpload"
                disabled={!isEditable}
              />
              <label
                htmlFor="FileUpload"
                className={clsx('attach-file-link-button bg-sub100 ', {
                  disabled: !isEditable,
                })}
              >
                {t('text:Attach_File')}
              </label>
              <div id="fileName" className="upload-file-input ps-3">
                {fileName ? fileName : t('text:No_file_attached')}
                {errors['formAttachment'] && <FontAwesomeIcon icon={faCheckCircle} className="check-icon ms-2" />}
              </div>
              <IconButton
                onClick={handleClickRemoveFile}
                className={`delete-uploaded-excel-button ${(!fileName || !isEditable) && 'd-none'}`}
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </IconButton>
            </div>
          </div>
        </FormBorder>
        {renderEditSaveBtn()}
      </div>
    </form>
  );
};

export default FinancierAdditionalDocumentDetail;
